/* eslint-disable import/prefer-default-export */
/* eslint-disable no-new */
import store from '@/store';
import router from '@/router';

import Config from '../Config';
import { FaceTecSDK } from '../../../../public/core-sdk/FaceTecSDK.js/FaceTecSDK';
import Logger from '../../../lib/logger';
import { useSelfieParcelexFallback } from '../utilities/fallbackFlow';

const FACETEC_ERROR_LIMIT = 1;

function handleUnsuspectedError(error, pageName = 'onboarding-facetec-error') {
  const urlSearchParams = new URLSearchParams(window.location.search);
  const query = Object.fromEntries(urlSearchParams.entries());

  Logger.error(error);

  if (pageName === 'onboarding-error-generic' && store.getters['onboarding/facetecErrorCount'] < FACETEC_ERROR_LIMIT) {
    query.errorTag = 'facetec-error';
    store.commit('onboarding/incrementFacetecErrorCount');
    return router.replace({
      name: pageName,
      query,
    });
  }

  return router.replace({
    name: 'onboarding-facetec-error',
    query,
  });
}

export class LivenessCheckProcessor {
  latestNetworkRequest = new XMLHttpRequest();

  latestSessionResult;

  success;

  sampleAppControllerReference;

  onboarding;

  frontendOnboardingVersion;

  constructor(sessionToken, sampleAppControllerReference) {
    this.success = false;
    this.sampleAppControllerReference = sampleAppControllerReference;
    this.latestSessionResult = null;
    this.onboarding = null;
    const isSuperOnboarding = window.location.pathname.includes('super-onboarding');
    const parcelexOnboardingVersion = window.location.pathname.includes('onboarding-v2') || window.location.pathname.includes('link-de-pagamento') ? 'v2' : 'v1';
    this.frontendOnboardingVersion = isSuperOnboarding ? 'minified' : parcelexOnboardingVersion;

    new FaceTecSDK.FaceTecSession(
      this,
      sessionToken,
    );
  }

  async processSessionResultWhileFaceTecSDKWaits(sessionResult, faceScanResultCallback) {
    this.latestSessionResult = sessionResult;

    if (sessionResult.status !== FaceTecSDK.FaceTecSessionStatus.SessionCompletedSuccessfully) {
      const faceTecSessionStatus = FaceTecSDK.FaceTecSessionStatus[sessionResult.status];
      const errorMessage = `Session was not completed successfully, cancelling.  Session Status: ${faceTecSessionStatus}`;

      this.latestNetworkRequest.abort();
      faceScanResultCallback.cancel();

      const { useFallback } = await useSelfieParcelexFallback({
        frontendOnboardingVersion: this.frontendOnboardingVersion,
        error: faceTecSessionStatus,
      });

      if (useFallback) return;

      handleUnsuspectedError(new Error(errorMessage), faceTecSessionStatus === 'UserCancelled'
        ? 'onboarding-error-generic' : '');

      return;
    }

    const parameters = {
      faceScan: sessionResult.faceScan,
      auditTrailImage: sessionResult.auditTrail[0],
      lowQualityAuditTrailImage: sessionResult.lowQualityAuditTrail[0],
      sessionId: sessionResult.sessionId,
      selfieFlow: 'FACETEC',
    };

    let onboardingId = store.getters['onboarding/onboardingId'];

    if (this.frontendOnboardingVersion === 'minified') {
      onboardingId = store.getters['minifiedOnboarding/onboardingId'];
    } if (this.frontendOnboardingVersion === 'v2') {
      onboardingId = store.getters['onboardingV2/onboardingId'];
    }

    const token = store.getters['auth/token'];

    let onboardingApi = '/onboarding/v2/';

    if (this.frontendOnboardingVersion === 'minified') {
      onboardingApi = '/onboarding/v3/';
    }

    this.latestNetworkRequest = new XMLHttpRequest();

    this.latestNetworkRequest.open('POST', `${Config.BaseURL}${onboardingApi}${onboardingId}/selfie/send`);
    this.latestNetworkRequest.setRequestHeader('Content-Type', 'application/json');
    this.latestNetworkRequest.setRequestHeader('Authorization', `Bearer ${token}`);

    // eslint-disable-next-line consistent-return
    this.latestNetworkRequest.onreadystatechange = () => {
      if (this.latestNetworkRequest.readyState === XMLHttpRequest.DONE) {
        try {
          const responseJSON = JSON.parse(this.latestNetworkRequest.responseText);

          const { scanResultBlob, onboarding } = responseJSON;

          this.onboarding = onboarding;

          if (responseJSON.wasProcessed) {
            if (this.onboarding.step === 'REFUSED' && this.frontendOnboardingVersion === 'minified') {
              const urlSearchParams = new URLSearchParams(window.location.search);
              const query = Object.fromEntries(urlSearchParams.entries());
              return router.replace({
                name: 'onboarding-minified-credit-not-available',
                query,
              });
            }
            FaceTecSDK.FaceTecCustomization.setOverrideResultScreenSuccessMessage('Foto\nConfirmada');

            faceScanResultCallback.proceedToNextStep(scanResultBlob);
          } else {
            const errorMessage = 'Unexpected API response, cancelling out.';

            console.log(errorMessage);
            faceScanResultCallback.cancel();

            if (this.onboarding.step === 'REFUSED' && this.frontendOnboardingVersion === 'minified') {
              return router.replace({
                name: 'onboarding-minified-credit-not-available',
              });
            } if (this.frontendOnboardingVersion === 'minified') {
              handleUnsuspectedError(new Error(errorMessage), 'onboarding-minified-selfie-error');
            } else {
              handleUnsuspectedError(new Error(errorMessage), 'onboarding-error-generic');
            }
          }
        } catch (error) {
          console.log(error);
          faceScanResultCallback.cancel();
          if (this.frontendOnboardingVersion === 'minified') {
            handleUnsuspectedError(error, 'onboarding-minified-selfie-error');
          } else {
            handleUnsuspectedError(error, 'onboarding-error-generic');
          }
        }
      }
    };

    this.latestNetworkRequest.onerror = () => {
      const errorMessage = 'XHR error, cancelling.';
      console.log(errorMessage);
      faceScanResultCallback.cancel();
      if (this.frontendOnboardingVersion === 'minified') {
        handleUnsuspectedError(new Error(errorMessage), 'onboarding-minified-selfie-error');
      } else {
        handleUnsuspectedError(new Error(errorMessage), 'onboarding-error-generic');
      }
    };

    this.latestNetworkRequest.upload.onprogress = (event) => {
      const progress = event.loaded / event.total;
      faceScanResultCallback.uploadProgress(progress);
    };

    const jsonStringToUpload = JSON.stringify(parameters);
    this.latestNetworkRequest.send(jsonStringToUpload);

    window.setTimeout(() => {
      if (this.latestNetworkRequest.readyState === XMLHttpRequest.DONE) {
        return;
      }
      faceScanResultCallback.uploadMessageOverride('Still Uploading...');
    }, 6000);
  }

  onFaceTecSDKCompletelyDone = () => {
    this.success = this.latestSessionResult.isCompletelyDone;

    if (this.onboarding && this.success) {
      const urlSearchParams = new URLSearchParams(window.location.search);
      const query = Object.fromEntries(urlSearchParams.entries());

      store.commit('onboarding/updateStep', { step: this.onboarding.step });

      let nextRoute = this.onboarding.step === 'REFUSED' ? 'onboarding-denied' : 'onboarding-insurance';

      if (this.frontendOnboardingVersion === 'minified') {
        nextRoute = 'onboarding-minified-installments-expire';
      } if (this.frontendOnboardingVersion === 'v2') {
        nextRoute = 'v2-onboarding-insurance';
      }

      router.replace({
        name: nextRoute,
        query,
      });
    }

    this.sampleAppControllerReference
      .onComplete(this.latestSessionResult, null, this.latestNetworkRequest.status);
  }

  isSuccess = () => this.success
}
